/**
 * ai-chat-text.ts — alle tekster i AI-chatten (inAI), norsk og engelsk
 *
 * Norsk er ordrett det chatten alltid har sagt. Carbon AI Chat har ikke norsk, så Carbons egne indre tekster («Send», «Stop response» …) er engelske i begge språk.
 * Hilsen og forslag kan overstyres med `intro` på AiChatDrawer (Shell2 bruker forslag som mockupen kan svare på).
 */

import type { AiChatLocale } from '@/types/ai';

export interface AiChatText {
  /** aria-label på chatten og på dra-håndtaket */
  drawerLabel: string;
  resizeLabel: string;
  assistantName: string;
  headerName: string;
  greeting: string;
  starters: string[];
  inputPlaceholder: string;
  newChat: string;
  viewChats: string;
  /** Stoppknappen mens AI-en jobber, og beskjeden når svaret er stoppet */
  stopResponse: string;
  responseStopped: string;
  /** Knappene i chatheaderen */
  actions: { chats: string; newChat: string; settings: string; normalWidth: string; fullWidth: string };
  /** Tenketrinn og verktøytrinn */
  steps: {
    thinking: string;
    thinkingRound: (round: number) => string;
    tool: (name: string) => string;
    pending: string;
    done: string;
    failed: string;
    stopped: string;
    answer: string;
  };
  /** Samtalepanelet */
  history: {
    title: string;
    close: string;
    newChat: string;
    results: string;
    loading: string;
    retry: string;
    noMatch: string;
    none: string;
    today: string;
    yesterday: string;
    last7: string;
    older: string;
  };
  /** AI-innstillinger (startsiden i panelet) */
  settings: {
    title: string;
    intro: string;
    mcpDescription: string;
    /** Navn på dra-streken øverst i panelet */
    resizeLabel: string;
  };
  /** MCP-serverne (under AI-innstillinger) */
  mcp: {
    title: string;
    intro: string;
    add: string;
    loading: string;
    retry: string;
    none: string;
    active: string;
    inactive: string;
    toggleOn: string;
    toggleOff: string;
    toggleLabel: (name: string) => string;
    auth: (type: string) => string;
    edit: string;
    remove: string;
    confirmRemove: (name: string) => string;
    cancel: string;
    formAdd: string;
    formEdit: string;
    name: string;
    url: string;
    description: string;
    authType: string;
    authNone: string;
    authKey: string;
    activeField: string;
    save: string;
    errLoad: string;
    errSave: string;
    errRemove: string;
  };
}

export const AI_CHAT_TEXT: Record<AiChatLocale, AiChatText> = {
  no: {
    drawerLabel: 'AI-assistent',
    resizeLabel: 'Endre bredde på chatten',
    assistantName: 'inAI',
    headerName: 'by Nevral',
    greeting: 'Hei! Hva kan jeg hjelpe deg med i dag?',
    starters: ['Hvilke verktøy har du?', 'Hvordan er været?', 'Trick or threat'],
    inputPlaceholder: 'Spør inAI om noe…',
    newChat: 'Ny samtale',
    viewChats: 'Vis samtaler',
    stopResponse: 'Stopp',
    responseStopped: 'Du stoppet svaret',
    actions: { chats: 'Samtaler', newChat: 'Ny samtale', settings: 'AI-innstillinger', normalWidth: 'Normal bredde', fullWidth: 'Full bredde' },
    steps: {
      thinking: 'Tenker',
      thinkingRound: (round) => `Tenker – runde ${round}`,
      tool: (name) => `Verktøy: ${name}`,
      pending: 'Pågår …',
      done: 'Ferdig',
      failed: 'Feilet',
      stopped: 'Stoppet',
      answer: 'Svar',
    },
    history: {
      title: 'Samtaler',
      close: 'Lukk samtaler',
      newChat: 'Ny samtale',
      results: 'Treff',
      loading: 'Henter samtaler…',
      retry: 'Prøv igjen',
      noMatch: 'Ingen samtaler matcher søket.',
      none: 'Ingen tidligere samtaler.',
      today: 'I dag',
      yesterday: 'I går',
      last7: 'Siste 7 dager',
      older: 'Eldre',
    },
    settings: {
      title: 'AI-innstillinger',
      intro: 'Alt som har med inAI å gjøre.',
      mcpDescription: 'Koble inAI til eksterne verktøyservere.',
      resizeLabel: 'Endre høyde på innstillingene',
    },
    mcp: {
      title: 'MCP-servere',
      intro: 'Servere inAI kan hente verktøy fra.',
      add: 'Legg til server',
      loading: 'Henter servere…',
      retry: 'Prøv igjen',
      none: 'Ingen MCP-servere er satt opp.',
      active: 'Aktiv',
      inactive: 'Inaktiv',
      toggleOn: 'På',
      toggleOff: 'Av',
      toggleLabel: (name) => `${name} aktiv`,
      auth: (type) => `Autentisering: ${type}`,
      edit: 'Rediger',
      remove: 'Slett',
      confirmRemove: (name) => `Slette ${name}?`,
      cancel: 'Avbryt',
      formAdd: 'Ny MCP-server',
      formEdit: 'Rediger MCP-server',
      name: 'Navn',
      url: 'URL',
      description: 'Beskrivelse',
      authType: 'Autentisering',
      authNone: 'Ingen',
      authKey: 'Nøkkel',
      activeField: 'Aktiv',
      save: 'Lagre',
      errLoad: 'Kunne ikke hente servere',
      errSave: 'Kunne ikke lagre',
      errRemove: 'Kunne ikke slette',
    },
  },
  en: {
    drawerLabel: 'AI assistant',
    resizeLabel: 'Change the width of the chat',
    assistantName: 'inAI',
    headerName: 'by Nevral',
    greeting: 'Hi! How can I help you today?',
    starters: ['Which tools do you have?', 'How is the weather?', 'Trick or threat'],
    inputPlaceholder: 'Ask inAI anything…',
    newChat: 'New chat',
    viewChats: 'View chats',
    stopResponse: 'Stop',
    responseStopped: 'You stopped the response',
    actions: { chats: 'Chats', newChat: 'New chat', settings: 'AI settings', normalWidth: 'Normal width', fullWidth: 'Full width' },
    steps: {
      thinking: 'Thinking',
      thinkingRound: (round) => `Thinking – round ${round}`,
      tool: (name) => `Tool: ${name}`,
      pending: 'In progress …',
      done: 'Done',
      failed: 'Failed',
      stopped: 'Stopped',
      answer: 'Response',
    },
    history: {
      title: 'Chats',
      close: 'Close chats',
      newChat: 'New chat',
      results: 'Results',
      loading: 'Loading chats…',
      retry: 'Try again',
      noMatch: 'No chats match the search.',
      none: 'No earlier chats.',
      today: 'Today',
      yesterday: 'Yesterday',
      last7: 'Last 7 days',
      older: 'Older',
    },
    settings: {
      title: 'AI settings',
      intro: 'Everything to do with inAI.',
      mcpDescription: 'Connect inAI to external tool servers.',
      resizeLabel: 'Change the height of the settings',
    },
    mcp: {
      title: 'MCP servers',
      intro: 'Servers inAI can get tools from.',
      add: 'Add server',
      loading: 'Loading servers…',
      retry: 'Try again',
      none: 'No MCP servers are set up.',
      active: 'Active',
      inactive: 'Inactive',
      toggleOn: 'On',
      toggleOff: 'Off',
      toggleLabel: (name) => `${name} active`,
      auth: (type) => `Authentication: ${type}`,
      edit: 'Edit',
      remove: 'Delete',
      confirmRemove: (name) => `Delete ${name}?`,
      cancel: 'Cancel',
      formAdd: 'New MCP server',
      formEdit: 'Edit MCP server',
      name: 'Name',
      url: 'URL',
      description: 'Description',
      authType: 'Authentication',
      authNone: 'None',
      authKey: 'Key',
      activeField: 'Active',
      save: 'Save',
      errLoad: 'Could not load servers',
      errSave: 'Could not save',
      errRemove: 'Could not delete',
    },
  },
};
